import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '../../components/ui/Toast';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/config';

const StoreManagement = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchStore();
  }, [slug]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/stores/${slug}`);
      
      if (response.data.success) {
        setStore(response.data.data);
      } else {
        showToast(response.data.message || 'Failed to load store', 'error');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load store';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const handleToggleStatus = async (storeId) => {
    try {
      const response = await api.patch(`/stores/${storeId}/toggle-status`);
      if (response.data.success) {
        setStore(prevStore => ({
          ...prevStore,
          status: prevStore.status === 'active' ? 'inactive' : 'active'
        }));
        showToast(response.data.message || 'Store status updated', 'success');
      } else {
        showToast(response.data.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling store status:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update status';
      showToast(errorMessage, 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Store Not Found</h2>
          <p className="text-gray-600 mb-6">The store you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/stores')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Stores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Store: {store.name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage store details and about sections
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/stores')}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
        >
          ← Back to Stores
        </button>
      </div>

      {/* Store Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          {/* Store Icon */}
          <div className="flex-shrink-0">
            {store.icon ? (
              <img
                src={getImageUrl(store.icon)}
                alt={`${store.name} icon`}
                className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                onError={e => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = `<svg xmlns='http://www.w3.org/2000/svg' class='w-16 h-16 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'><rect width='100%' height='100%' rx='8' fill='#f3f4f6'/><path stroke='#9ca3af' strokeWidth='2' d='M8 16h8M8 12h8m-8-4h8'/></svg>`;
                }}
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-400 text-2xl">🏪</span>
              </div>
            )}
          </div>

          {/* Store Details */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{store.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Email:</span> {store.email}
                </p>
                {store.contact_email && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Contact Email:</span> {store.contact_email}
                  </p>
                )}
                {store.phone && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Phone:</span> {store.phone}
                  </p>
                )}
              </div>
              <div>
                {store.location && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Location:</span> {store.location}
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Status:</span> 
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                    store.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {store.status}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Slug:</span> {store.slug}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{store.categories?.length || 0}</div>
            <div className="text-sm text-blue-600">Categories</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{store.products?.length || 0}</div>
            <div className="text-sm text-green-600">Products</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">{store.about_sections?.length || 0}</div>
            <div className="text-sm text-purple-600">About Sections</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Store Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => navigate(`/admin/stores/${store.slug}/edit`)}
            className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span className="mr-2">✏️</span>
            Edit Store
          </button>
          
          <button
            onClick={() => navigate(`/admin/stores/${store.slug}/about`)}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <span className="mr-2">📝</span>
            Manage About Sections
          </button>
          
          <button
            onClick={() => navigate(`/admin/stores/${store.slug}/categories`)}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <span className="mr-2">📁</span>
            Manage Categories
          </button>
          
          <button
            onClick={() => navigate(`/admin/stores/${store.slug}/products`)}
            className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <span className="mr-2">📦</span>
            Manage Products
          </button>
          
          <button
            onClick={() => handleToggleStatus(store.id)}
            className={`flex items-center justify-center px-4 py-3 rounded-lg transition-colors ${
              store.status === 'active'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <span className="mr-2">{store.status === 'active' ? '🚫' : '✅'}</span>
            {store.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default StoreManagement; 